import chroma from 'chroma-js';
import { SEMANTIC_COLORS, PRIMARY_COLORS } from '../../../constants/colors';
import { closedEdgeUids } from './interventions';

const FLOW_SCALE = ["#ffffd9", "#edf8b1", "#c7e9b4", "#a1dab4", "#7fcdbb", "#41b6c4", "#1d91c0", "#225ea8", "#253494", "#081d58"];

export function getFirstName(name) {
  if (!name) return 'Strada';
  let str = name;
  if (typeof name === 'string' && name.trim().startsWith('[') && name.trim().endsWith(']')) {
    const match = name.match(/['"](.*?)['"]/);
    if (match) str = match[1];
  }
  if (Array.isArray(str)) str = str[0];
  else if (typeof str === 'string' && str.includes('/')) str = str.split('/')[0].trim();
  return str;
}

export function metricValue(props, metric, simulated) {
  if (!props) return 0;
  if (metric === 'vc') return Number(props.vc_ratio ?? props.vc_scenario ?? 0);
  if (metric === 'capacity') return Number(props.capacity_vph ?? 0);
  if (metric === 'speed') return Number(props.speed_kph ?? props.speed_bpr ?? 0);
  if (metric === 'travel_time') return Number(props.travel_time_bpr ?? props.travel_time ?? 0);
  if (metric === 'delta_flow') return Number(props.delta_flow ?? 0);
  return Number(simulated ? (props.flow_scenario ?? props.flow ?? 0) : (props.flow ?? 0));
}

export function flowToColor(flow, maxFlow) {
  const t = Math.min(flow / Math.max(maxFlow, 1), 1);
  return chroma.scale(FLOW_SCALE)(t).hex();
}

export function flowToWeight(flow, maxFlow) {
  return 2 + Math.min(flow / Math.max(maxFlow, 1), 1) * 6;
}

export function vcColor(v) {
  if (v > 1) return SEMANTIC_COLORS.danger;
  if (v > 0.8) return SEMANTIC_COLORS.warning;
  if (v > 0.6) return '#c9a400';
  return SEMANTIC_COLORS.success;
}

export const INTERVENTION_COLORS = {
  closed: SEMANTIC_COLORS.danger,
  capacity: SEMANTIC_COLORS.warning,
  reverse: PRIMARY_COLORS.brandPurple,
  news: PRIMARY_COLORS.primary,
  open: SEMANTIC_COLORS.success,
};

export const CAP_PER_LANE = {
  motorway: 2000,
  motorway_link: 1800,
  trunk: 1500,
  trunk_link: 1300,
  primary: 1000,
  primary_link: 900,
  secondary: 800,
  secondary_link: 700,
  tertiary: 600,
  tertiary_link: 500,
  unclassified: 450,
  residential: 400,
  living_street: 250,
  service: 250,
};

export function estimateCapacityVph(highway, lanes) {
  return Math.max(1, (Number(lanes) || 1) * (CAP_PER_LANE[highway] || 600));
}

export const KPI_THRESHOLDS = {
  critical_vc: 0.85,
  saturated_vc: 1,
  high_delta_flow: 100,
};

function edgeRecord(feature) {
  const p = feature?.properties || {};
  const uid = String(p.edge_uid || feature?.id || '');
  const parts = uid.split('_');
  return {
    uid,
    u: String(p.u ?? parts[0] ?? ''),
    v: String(p.v ?? parts[1] ?? ''),
    flow: Number(p.flow_scenario ?? p.flow ?? 0),
    capacity: Math.max(1, Number(p.capacity_vph ?? p.capacity ?? 600)),
    isNew: !!p.is_new,
  };
}

export function edgeKpisFrom(pairs) {
  const loaded = (pairs || []).filter((x) => Number(x.flow) > 0);
  if (!loaded.length) {
    return {
      loaded_edges: 0, total_flow_on_edges: 0, max_flow: 0,
      mean_vc_loaded: 0, max_vc: 0, critical_edges: 0, saturated_edges: 0,
    };
  }
  const vcs = loaded.map((x) => Number(x.vc) || 0);
  return {
    loaded_edges: loaded.length,
    total_flow_on_edges: loaded.reduce((sum, x) => sum + Number(x.flow || 0), 0),
    max_flow: Math.max(...loaded.map((x) => Number(x.flow || 0))),
    mean_vc_loaded: vcs.reduce((sum, value) => sum + value, 0) / loaded.length,
    max_vc: Math.max(...vcs),
    critical_edges: vcs.filter((v) => v >= KPI_THRESHOLDS.critical_vc).length,
    saturated_edges: vcs.filter((v) => v >= KPI_THRESHOLDS.saturated_vc).length,
  };
}

export function deltaKpisFrom(baseFlowByUid, scenarioFlowByUid) {
  const uids = new Set([...Object.keys(baseFlowByUid || {}), ...Object.keys(scenarioFlowByUid || {})]);
  const result = {
    edges_with_flow_increase: 0, edges_with_flow_decrease: 0,
    max_delta_flow: 0, min_delta_flow: 0, edges_high_increase: 0,
  };
  uids.forEach((uid) => {
    const delta = Number(scenarioFlowByUid?.[uid] || 0) - Number(baseFlowByUid?.[uid] || 0);
    if (delta > 0) result.edges_with_flow_increase += 1;
    if (delta < 0) result.edges_with_flow_decrease += 1;
    result.max_delta_flow = Math.max(result.max_delta_flow, delta);
    result.min_delta_flow = Math.min(result.min_delta_flow, delta);
    if (delta >= KPI_THRESHOLDS.high_delta_flow) result.edges_high_increase += 1;
  });
  return result;
}

export function impactIndexFrom(scenario, delta) {
  let score = 0;
  score += Math.min(40, Number(scenario?.saturated_edges || 0) * 4);
  score += Math.min(30, Number(scenario?.critical_edges || 0) * 2);
  score += Math.min(20, Number(delta?.edges_high_increase || 0) * 2);
  score += Math.min(10, Math.max(0, Number(scenario?.max_vc || 0) - 1) * 10);
  score = Math.round(score * 100) / 100;
  return { score, label: score >= 70 ? 'alto' : score >= 35 ? 'medio' : 'basso' };
}

export function simulateScenarioFlows(features, interventions = {}, newRoads = [], edgeMeta = {}) {
  const edges = (features || []).filter((f) => f.geometry?.type === 'LineString').map(edgeRecord);
  (newRoads || []).forEach((road) => {
    const base = {
      flow: Number(road.flow || 0),
      capacity: Math.max(1, Number(road.capacity || road.capacity_vph || 600)),
      isNew: true,
    };
    edges.push({ ...base, uid: road.uid || road.temp_id, u: String(road.origin ?? road.graph_u), v: String(road.destination ?? road.graph_v) });
    if (road.twoWay || road.two_way) {
      edges.push({ ...base, uid: road.opposite_uid || `${road.uid || road.temp_id}_reverse`, u: String(road.destination ?? road.graph_v), v: String(road.origin ?? road.graph_u) });
    }
  });
  const flow = Object.fromEntries(edges.map((edge) => [edge.uid, edge.flow]));
  const effectiveCapacity = (edge) => {
    const action = interventions[edge.uid];
    if (!action) return edge.capacity;
    const base = action.newCap != null ? Number(action.newCap) : edge.capacity;
    return Math.max(1, Math.round(base * (Number(action.factor) || 1)));
  };
  const closed = closedEdgeUids(interventions, edgeMeta);
  const isClosed = (uid) => closed.has(uid);
  const nodeMap = {};
  edges.forEach((edge) => {
    (nodeMap[edge.u] ||= []).push(edge);
    (nodeMap[edge.v] ||= []).push(edge);
  });
  edges.filter((edge) => isClosed(edge.uid) && flow[edge.uid] > 0).forEach((edge) => {
    const donation = flow[edge.uid];
    flow[edge.uid] = 0;
    const nearby = [...new Set([...(nodeMap[edge.u] || []), ...(nodeMap[edge.v] || [])])]
      .filter((candidate) => candidate.uid !== edge.uid && !isClosed(candidate.uid));
    if (!nearby.length) return;
    const headroom = nearby.reduce((sum, candidate) => sum + Math.max(50, effectiveCapacity(candidate) - flow[candidate.uid]), 0) || 1;
    nearby.forEach((candidate) => {
      const share = Math.max(50, effectiveCapacity(candidate) - flow[candidate.uid]) / headroom;
      flow[candidate.uid] = Math.min(effectiveCapacity(candidate) * 1.4, flow[candidate.uid] + donation * share);
    });
  });
  return { edges, flow, effectiveCapacity, isClosed };
}

export function computeScenarioKPIs(features, interventions = {}, newRoads = [], simulatedFeatures = null, edgeMeta = {}) {
  const baseEdges = (features || []).filter((f) => f.geometry?.type === 'LineString' && !f.properties?.is_new).map(edgeRecord);
  const baseFlow = Object.fromEntries(baseEdges.map((edge) => [edge.uid, edge.flow]));
  const base = edgeKpisFrom(baseEdges.map((edge) => ({ flow: edge.flow, vc: edge.flow / edge.capacity })));
  let scenarioEdges;
  let scenarioFlow;
  let effectiveCapacity;
  let isClosed;

  if (simulatedFeatures?.length) {
    scenarioEdges = simulatedFeatures.filter((f) => f.geometry?.type === 'LineString').map(edgeRecord);
    scenarioFlow = Object.fromEntries(scenarioEdges.map((edge) => [edge.uid, edge.flow]));
    effectiveCapacity = (edge) => edge.capacity;
    isClosed = () => false;
  } else {
    const heuristic = simulateScenarioFlows(features, interventions, newRoads, edgeMeta);
    ({ edges: scenarioEdges, flow: scenarioFlow, effectiveCapacity, isClosed } = heuristic);
  }
  const open = scenarioEdges.filter((edge) => !isClosed(edge.uid));
  const scenario = edgeKpisFrom(open.map((edge) => ({
    flow: scenarioFlow[edge.uid] || 0,
    vc: (scenarioFlow[edge.uid] || 0) / effectiveCapacity(edge),
  })));
  const delta = deltaKpisFrom(baseFlow, Object.fromEntries(open.map((edge) => [edge.uid, scenarioFlow[edge.uid] || 0])));
  const capacitiesModified = Object.entries(interventions).filter(([uid, action]) => {
    const edge = scenarioEdges.find((candidate) => candidate.uid === uid);
    return (action.newCap != null && Number(action.newCap) !== Number(edge?.capacity)) || Number(action.factor ?? 1) !== 1;
  }).length;
  const network = {
    closed_edges_removed: Object.values(interventions).filter((a) => a.forward || a.both).length,
    capacities_modified: capacitiesModified,
    edges_reversed: Object.values(interventions).filter((a) => a.reverse).length,
    edges_added: newRoads.reduce((sum, road) => sum + ((road.twoWay || road.two_way) ? 2 : 1), 0),
  };
  return { base, scenario, delta, impact_index: impactIndexFrom(scenario, delta), network };
}
